import { DayOfWeek, LocalDate } from "@js-joda/core";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FC, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { MonthPeriodNavigator } from "components/designV2/periodNavigator/monthPeriodNavigator.component";
import { ScrollSyncProvider, useScrollSyncGroup } from "hooks/useScrollSync";
import { ScheduleDayShiftsPanel } from "web-apps/company/components/schedule/components/common/dayShiftsPanel/scheduleDayShiftsPanel.component";
import {
  ScheduleRow,
  type ScheduleCreatePayload,
  type ShiftClickPayload,
} from "web-apps/company/components/schedule/components/common/scheduleRow/scheduleRow.component";
import { GroupDivider } from "web-apps/company/components/schedule/components/views/shared/groupDivider.component";
import { getRowRenderKey } from "web-apps/company/components/schedule/components/views/shared/scheduleRow.key.logic";
import { ScheduleViewBody } from "web-apps/company/components/schedule/components/views/shared/scheduleViewBody.component";
import type {
  GroupByOption,
  ScheduleWorkerRow,
} from "web-apps/company/components/schedule/types/schedule.types";
import {
  formatDateKey,
  formatItemDateKey,
  getIsoWeekNumber,
  getStockholmLocalDateNow,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import {
  DayNumber,
  DaySlot,
  MonthHeader,
  MonthScrollContainer,
  MonthView,
  MonthRow,
  MonthRowSpacer,
  MonthHeaderCard,
  StickyDatesBar,
  StickyDatesSentinel,
  WeekGroup,
  WeekNumber,
} from "./scheduleMonthView.styled";

interface Props {
  readonly currentDate: LocalDate;
  readonly emptyText: string;
  readonly groupByOption: GroupByOption;
  readonly isEmpty: boolean;
  readonly isLoading: boolean;
  readonly isLoadingMore: boolean;
  readonly loadMoreRef: RefObject<HTMLDivElement>;
  readonly shiftBlockSize: "small" | "large" | "pill";
  readonly workerRows: readonly ScheduleWorkerRow[];
  readonly onChangeDate: (nextDate: LocalDate) => void;
  readonly onScheduleClick: (payload: ScheduleCreatePayload) => void;
  readonly onShiftClick: (payload: ShiftClickPayload) => void;
}

interface MonthDatesRowProps {
  readonly monthDays: readonly LocalDate[];
  readonly monthWeekGroups: readonly MonthWeekGroup[];
  readonly today: LocalDate;
  readonly onDayClick: (date: LocalDate) => void;
  readonly openDayShiftsLabel: (date: LocalDate) => string;
  readonly weekNumberLabel: (week: number) => string;
}

interface MonthWeekGroup {
  readonly dayCount: number;
  readonly firstDate: LocalDate;
}

const getMonthWeekGroups = (
  monthDays: readonly LocalDate[],
): readonly MonthWeekGroup[] => {
  const weekStarts = monthDays
    .map((firstDate, monthDayIndex) => ({ firstDate, monthDayIndex }))
    .filter(
      (entry) =>
        entry.monthDayIndex === 0 ||
        entry.firstDate.dayOfWeek() === DayOfWeek.MONDAY,
    );

  return weekStarts.map((entry, weekIndex) => ({
    firstDate: entry.firstDate,
    dayCount:
      (weekStarts[weekIndex + 1]?.monthDayIndex ?? monthDays.length) -
      entry.monthDayIndex,
  }));
};

const MonthDatesRow: FC<MonthDatesRowProps> = (props) => (
  <MonthRow $dayCount={props.monthDays.length}>
    <MonthRowSpacer />
    {props.monthWeekGroups.map((weekGroup) => {
      const weekNumberText = props.weekNumberLabel(
        getIsoWeekNumber(weekGroup.firstDate),
      );
      return (
        <WeekGroup
          key={weekGroup.firstDate.toString()}
          $dayCount={weekGroup.dayCount}
        >
          <WeekNumber aria-label={weekNumberText}>{weekNumberText}</WeekNumber>
        </WeekGroup>
      );
    })}
    {props.monthDays.map((date) => {
      const isWeekend =
        date.dayOfWeek() === DayOfWeek.SATURDAY ||
        date.dayOfWeek() === DayOfWeek.SUNDAY;
      const isToday = date.equals(props.today);
      return (
        <DaySlot
          key={date.toString()}
          $isToday={isToday}
          role="button"
          tabIndex={0}
          aria-label={props.openDayShiftsLabel(date)}
          onClick={() => props.onDayClick(date)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              props.onDayClick(date);
            }
          }}
        >
          <DayNumber $isWeekend={isWeekend} $isToday={isToday}>
            {date.dayOfMonth()}
          </DayNumber>
        </DaySlot>
      );
    })}
  </MonthRow>
);

export const ScheduleMonthView: FC<Props> = (props) => {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const headerScrollRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [selectedDay, setSelectedDay] = useState<LocalDate | null>(null);
  const { register } = useScrollSyncGroup();
  const today = getStockholmLocalDateNow();

  useEffect(() => register(headerScrollRef.current), [register]);
  const monthStart = useMemo(
    () => props.currentDate.withDayOfMonth(1),
    [props.currentDate],
  );
  const daysInMonth = monthStart.lengthOfMonth();

  const monthDays = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, index) =>
        monthStart.plusDays(index),
      ),
    [daysInMonth, monthStart],
  );
  const monthWeekGroups = useMemo(
    () => getMonthWeekGroups(monthDays),
    [monthDays],
  );
  const monthDayKeys = useMemo(
    () => new Set(monthDays.map((date) => formatDateKey(date))),
    [monthDays],
  );

  const visibleWorkerRows = useMemo(
    () =>
      props.workerRows.filter((workerRow) =>
        workerRow.sections.some((section) =>
          section.items.some((item) =>
            monthDayKeys.has(formatItemDateKey(item.startTime)),
          ),
        ),
      ),
    [monthDayKeys, props.workerRows],
  );
  const isViewEmpty = !props.isLoading && visibleWorkerRows.length === 0;

  const rowsWithDividers = useMemo<
    Array<{
      key: string;
      row: ScheduleWorkerRow;
      divider: JSX.Element | null;
    }>
  >(() => {
    if (props.groupByOption !== "team") {
      return visibleWorkerRows.map((workerRow) => ({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: null,
      }));
    }

    const result: Array<{
      key: string;
      row: ScheduleWorkerRow;
      divider: JSX.Element | null;
    }> = [];
    let lastWorkGroupId: number | null = null;
    visibleWorkerRows.forEach((workerRow) => {
      const section = workerRow.sections[0];
      const workGroupId = section?.id ?? null;
      const rawUnitName = (section?.unitName ?? "").trim();
      const unitName =
        rawUnitName.length > 0 ? rawUnitName : t("Schedule.UnknownUnit");
      const rawTeamName = (section?.text ?? "").trim();
      const teamName =
        rawTeamName.length > 0 ? rawTeamName : t("Schedule.UnknownTeam");
      const shouldRenderDivider =
        workGroupId !== null && workGroupId !== lastWorkGroupId;
      result.push({
        key: getRowRenderKey(workerRow, props.groupByOption),
        row: workerRow,
        divider: shouldRenderDivider ? (
          <GroupDivider
            key={`divider-${workGroupId}`}
            unitName={unitName}
            teamName={teamName}
          />
        ) : null,
      });
      lastWorkGroupId = workGroupId;
    });
    return result;
  }, [props.groupByOption, t, visibleWorkerRows]);

  useEffect(() => {
    const element = sentinelRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { rootMargin: "-16px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <ScrollSyncProvider value={register}>
      <MonthView>
        <MonthHeaderCard data-stuck={isStuck}>
          <MonthHeader>
            <MonthPeriodNavigator
              currentDate={props.currentDate}
              onChange={props.onChangeDate}
              showTodayButton
            />
          </MonthHeader>
        </MonthHeaderCard>
        <StickyDatesSentinel ref={sentinelRef} />
        <StickyDatesBar data-stuck={isStuck}>
          <MonthScrollContainer ref={headerScrollRef}>
            <MonthDatesRow
              monthDays={monthDays}
              monthWeekGroups={monthWeekGroups}
              today={today}
              weekNumberLabel={(week) => t("Schedule.WeekNumber", { week })}
              onDayClick={setSelectedDay}
              openDayShiftsLabel={(date) =>
                t("Schedule.OpenDayShifts", { date: date.toString() })
              }
            />
          </MonthScrollContainer>
        </StickyDatesBar>
        <ScheduleViewBody
          isLoading={props.isLoading}
          isEmpty={props.isEmpty || isViewEmpty}
          emptyText={props.emptyText}
          isLoadingMore={props.isLoadingMore}
          loadMoreRef={props.loadMoreRef}
          hasContent={visibleWorkerRows.length > 0}
          isDimmed={props.isLoading && visibleWorkerRows.length > 0}
        >
          {rowsWithDividers.map((entry) => (
            <div key={entry.key}>
              {entry.divider}
              <ScheduleRow
                workerRow={entry.row}
                days={monthDays}
                shiftBlockSize={props.shiftBlockSize}
                variant="month"
                groupByOption={props.groupByOption}
                onShiftClick={props.onShiftClick}
                onScheduleClick={props.onScheduleClick}
              />
            </div>
          ))}
        </ScheduleViewBody>
      </MonthView>
      <ScheduleDayShiftsPanel
        date={selectedDay}
        onClose={() => setSelectedDay(null)}
      />
    </ScrollSyncProvider>
  );
};
